import { IconProps } from '@chakra-ui/react';

import {
  ClipboardCloseOutline,
  EditOutline,
  PauseOutline,
  InviteOutline,
  ArchiveOutline,
  DeleteOutline,
  ReactivateOutline,
  NotifyOutline,
  ExportOutline,
  BriefcaseOutline,
  UserOctagonOutline,
  DocumentDownloadOutline,
  NoteAddOutline,
  NoteOutline,
  AddCircleOutline,
  CalendarOutline,
} from '@aslanpay/portal-common-lib';

export interface ActionValueType {
  id: string;
  icon: (props: IconProps) => JSX.Element;
  value: string;
}

export const defaultPageSize = 15;

export enum AdminField {
  friendlyId = 'Aslan ID',
  firstName = 'First name',
  lastName = 'Last name',
  email = 'Email',
  role = 'Role',
  status = 'Status',
}

export enum EmployeeField {
  friendlyId = 'Aslan Friendly ID',
  companyName = 'Company',
  firstName = 'First name',
  lastName = 'Last name',
  email = 'Email',
  status = 'Status',
}

export enum IndividualActionsIds {
  EDIT = 'edit',
  PAUSE = 'pause',
  REACTIVATE = 'reactivate',
  SET_UNPAID = 'setUnpaid',
  REMOVE_UNPAID = 'removeUnpaid',
  TERMINATE = 'terminate',
  EDIT_TERMINATE = 'editTerminate',
  NOTIFY = 'notify',
  INVITE = 'invite',
  EXPORT = 'export',
  REWARD_POINTS = 'rewardPoints',
  DELETE = 'delete',
}

export const individualActionsValues: ActionValueType[] = [
  { id: IndividualActionsIds.EDIT, icon: EditOutline, value: 'Edit profile' },
  { id: IndividualActionsIds.PAUSE, icon: PauseOutline, value: 'Pause account' },
  { id: IndividualActionsIds.REACTIVATE, icon: ReactivateOutline, value: 'Reactivate account' },
  { id: IndividualActionsIds.SET_UNPAID, icon: ClipboardCloseOutline, value: 'Set accrual pause' },
  {
    id: IndividualActionsIds.REMOVE_UNPAID,
    icon: ClipboardCloseOutline,
    value: 'Remove accrual pause',
  },
  { id: IndividualActionsIds.TERMINATE, icon: CalendarOutline, value: 'Set leave date' },
  { id: IndividualActionsIds.EDIT_TERMINATE, icon: CalendarOutline, value: 'Manage leave date' },
  { id: IndividualActionsIds.NOTIFY, icon: NotifyOutline, value: 'Notify' },
  { id: IndividualActionsIds.INVITE, icon: InviteOutline, value: 'Invite to Aslan' },
  { id: IndividualActionsIds.EXPORT, icon: ExportOutline, value: 'Export' },
  { id: IndividualActionsIds.REWARD_POINTS, icon: AddCircleOutline, value: 'Reward points' },
  { id: IndividualActionsIds.DELETE, icon: DeleteOutline, value: 'Delete' },
];

export enum CompanyField {
  name = 'Name',
  displayName = 'Display name',
  friendlyId = 'Aslan ID',
  createdAt = 'Created on',
  payCalendars = 'Pay calendars',
  employees = 'Employees',
  activeServices = 'Active services',
  status = 'Status',
}

export enum CompanyActionsIds {
  EDIT = 'edit',
  ADD_ADMIN = 'add',
  DELETE = 'delete',
}

export const companyActionsValues: ActionValueType[] = [
  { id: CompanyActionsIds.EDIT, icon: BriefcaseOutline, value: 'Go to company dashboard' },
  { id: CompanyActionsIds.ADD_ADMIN, icon: UserOctagonOutline, value: 'Add admin' },
  { id: CompanyActionsIds.DELETE, icon: DeleteOutline, value: 'Delete' },
];

export enum PayCalendarField {
  name = 'Pay calendar name',
  friendlyId = 'ID',
  payFrequency = 'Frequency',
  payType = 'Type',
  employees = 'No. of employees',
  odpAllowancePercentage = 'On-demand pay sub-limit',
  startingBalanceDays = '1st of month accrual',
  startDate = 'Period',
}

export enum PayCalendarActionsIds {
  ARCHIVE = 'archive',
  DELETE = 'delete',
}

export const payCalendarActionsValues: ActionValueType[] = [
  { id: PayCalendarActionsIds.ARCHIVE, icon: ArchiveOutline, value: 'Archive' },
  { id: PayCalendarActionsIds.DELETE, icon: DeleteOutline, value: 'Delete' },
];

export enum PayCycleField {
  period = 'Period',
  startDate = 'Start date',
  endDate = 'End date',
  cutoffDate = 'Cut-off date',
  payday = 'Payday',
  odpAllowancePercentage = 'On-demand pay sub-limit %',
  accrualStartDate = 'Accrual start date',
  accrualEndDate = 'Accrual end date',
  status = 'Stage',
}

export enum DeductionsReportsField {
  companyName = 'Company',
  id = 'ID',
  salaryDeductions = 'Deduction amount',
  totalRecords = 'Records',
  payCalendarName = 'Pay calendar name',
  payCyclePeriod = 'Pay cycle',
  createdAt = 'Creation date',
  updatedAt = 'Updated date',
  status = 'Status',
  integration = 'Integration',
}

export enum DeductionsReportsActionsIds {
  DOWNLOAD = 'download',
}

export const deductionsReportsActionsValues: ActionValueType[] = [
  {
    id: DeductionsReportsActionsIds.DOWNLOAD,
    icon: DocumentDownloadOutline,
    value: 'Download CSV',
  },
];

export enum MerchantField {
  id = 'ID',
  logoUrl = 'Logo',
  name = 'Merchant Name',
  description = 'Description',
  url = 'Affiliate URL',
  isPopular = 'On Homepage',
}

export enum VoucherBrandsField {
  logoUrl = 'Logo',
  name = 'Brand Name',
  promoName = 'Promo Name',
  provider = 'Provider',
  priority = 'Priority',
  id = 'ID',
  merchantId = 'Merchant ID',
  promoId = 'Promo ID',
  promoStartDate = 'Promo Start Date',
  promoEndDate = 'Promo End Date',
  availableToUser = 'Available to User',
  availableToUserForPaymentGateway = 'Available to User for Payment Gateway',
  // Additional commission fields
  available = 'Available',
  tillo = 'Tillo',
  providerCommission = 'Provider Commission',
  paymentGatewayCommission = 'Payment Gateway Commission',
  availableToAslan = 'Available to Aslan',
  availableToAslanPaymentGateway = 'Available to Aslan Payment Gateway',
}

export enum TransactionField {
  createdAt = 'Created at',
  settledAt = 'Settled at',
  transactionCategory = 'Category',
  totalAmount = 'Tot. amt',
  balance = 'Balance',
  transactionStatus = 'Status',
  otherMerchant = 'Merchant',
  id = 'ID',
  externalId = 'External ID',
  mcc = 'MCC',
  externalCardId = 'Card ID',
  notes = 'Notes',
}

export enum SuspiciousTransactionColumns {
  id = 'ID',
  employeeName = 'Aslan customer',
  companyName = 'Company',
  externalId = 'Transaction link',
  createdAt = 'Created at',
  transactionType = 'Transaction type',
  transactionDirection = 'Transaction direction',
  transactionStatus = 'Transaction status',
  countryCode = 'Country',
  externalCardId = 'Card ID',
  otherMerchant = 'Merchant details',
  riskRuleCodes = 'Risk rules',
  amount = 'Amount',
  currency = 'Currency',
  originalAmount = 'Original amount',
  originalCurrency = 'Original currency',
  fxFee = 'FX fee',
  conversionRate = 'Conversion rate',
}

export enum SuspiciousTransactionsActionsIds {
  ADD_NOTE = 'addNote',
  VIEW_NOTES = 'viewNotes',
  EXPORT = 'export',
}

export const suspiciousTransactionsActionsValues: ActionValueType[] = [
  {
    id: SuspiciousTransactionsActionsIds.ADD_NOTE,
    icon: NoteAddOutline,
    value: 'Add note',
  },
  { id: SuspiciousTransactionsActionsIds.VIEW_NOTES, icon: NoteOutline, value: 'View notes' },
  {
    id: SuspiciousTransactionsActionsIds.EXPORT,
    icon: DocumentDownloadOutline,
    value: 'Export CSV',
  },
];

export enum IndividualDeductionsReportField {
  id = 'ID',
  companyName = 'Company',
  employeeName = 'Employee',
  payCycleName = 'Pay cycle',
  externalPayrollId = 'Payroll ID',
  salaryDeductions = 'Deductions amount',
  currency = 'Currency',
  transactionIds = 'Total transactions',
  status = 'Status',
}

// Required column headers for fps's EMPLOYEE_DATA_FILE type — mirrors
// io.aslan.fps.types.edf.model.EdfHeader's requiredAsColumnHeader=true set.
export enum ImportedEmployeeColumns {
  externalPayrollId = 'Payroll ID',
  firstName = 'First Name',
  lastName = 'Last Name',
  primaryEmail = 'Primary Email',
  secondaryEmail = 'Secondary Email',
  startDate = 'Start Date',
  probationEndDate = 'Probation End Date',
  salariedEmployee = 'Salaried Employee',
  annualGrossSalary = 'Gross Annual Salary',
  futureGrossAnnualSalaryAmount = 'Future Gross Annual Salary Amount',
  futureGrossAnnualSalaryAppliesDate = 'Future Gross Annual Salary Applies Date',
  currency = 'Currency',
}

// Required column headers for fps's GIFTING_ONLY_EMPLOYEE_DATA_FILE type — mirrors
// io.aslan.fps.types.giftingedf.model.GiftingEdfHeader.
export enum GiftingImportedEmployeeColumns {
  firstName = 'First Name',
  lastName = 'Last Name',
  companyEmail = 'Company Email',
  startDate = 'Start Date',
  leaveDate = 'Leave Date',
}

export enum ImportedAETColumns {
  externalPayrollId = 'Payroll ID',
  approvedEarnings = 'Approved Earnings',
  currency = 'Currency',
  unit = 'Unit',
  rate = 'Rate',
  basis = 'Basis',
  payday = 'Pay day',
}

export enum PointsTransactionColumns {
  createdAt = 'Created at',
  amount = 'Amount',
  balance = 'Balance',
  transactionCategory = 'Category',
  transactionReason = 'Reason',
  notes = 'Notification reason text',
  id = 'ID',
  moneyTransactionId = 'Money Tx ID',
}

export enum DeductionRequestsColumns {
  payCycleId = 'Period',
  status = 'Status',
  reason = 'Reason',
  amount = 'Amount',
  recurringAmount = 'Recurring amount',
  isRecurring = 'Recurring',
  completeOnDate = 'Complete on',
  completeNumDaysBeforePayday = 'Days before payday',
  notes = 'Notes',
  createdAt = 'Created',
  updatedAt = 'Updated',
  cancelledAt = 'Cancelled',
  id = 'Request ID',
  transactionId = 'Transaction ID',
}

export enum EarningsColumns {
  date = 'Date',
  grossEarnings = 'Gross earnings',
  netEarnings = 'Net earnings',
  status = 'Status',
}

export enum GiftedEmployeesColumns {
  fullName = 'Recipient name',
  email = 'Email',
  points = 'Points received',
}

export enum VoucherField {
  id = 'Voucher Id',
  promoName = 'Name',
  createdAt = 'Purchased',
  expiry = 'Expires',
  initialBalance = 'Value',
  currentBalance = 'Balance',
  pointsReceived = 'Points',
  paidWithAslan = 'Aslan',
  status = 'Status',
  // brandId = 'Brand',
  provider = 'Provider',
  internalTransactionId = 'Internal Txn Id',
  externalTransactionId = 'External Txn Id',
  externalTransactionDate = 'External Txn Date',
}

export enum PartnerField {
  name = 'Name',
  friendlyId = 'Aslan ID',
  createdAt = 'Created on',
  status = 'Status',
}

export enum PartnerActionsIds {
  EDIT = 'edit',
  DELETE = 'delete',
}

export const partnerActionsValues: ActionValueType[] = [
  { id: PartnerActionsIds.EDIT, icon: EditOutline, value: 'Edit partner' },
  { id: PartnerActionsIds.DELETE, icon: DeleteOutline, value: 'Delete partner' },
];
